import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Plus,
  Play,
  Eye,
  Edit3,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Save,
  X
} from 'lucide-react';
import { Button } from '../common/Button';
import { migrationApi } from '../../services/migrationApi';
import {
  SchemaMigration,
  MigrationDto,
  TenantMigrationStatus,
  MigrationRunResult
} from '../../types/Migration';

export const MigrationDashboard: React.FC = () => {
  const [migrations, setMigrations] = useState<SchemaMigration[]>([]);
  const [tenantStatuses, setTenantStatuses] = useState<TenantMigrationStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'migrations' | 'status' | 'run'>('migrations');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingMigration, setEditingMigration] = useState<SchemaMigration | null>(null);
  const [formData, setFormData] = useState<MigrationDto>({
    migrationId: '',
    description: '',
    sqlScript: '',
    sortOrder: 0,
    createdBy: '',
    isActive: true
  });

  // Run state
  const [runResult, setRunResult] = useState<MigrationRunResult | null>(null);
  const [running, setRunning] = useState(false);
  const [selectedTenantIds, setSelectedTenantIds] = useState<number[]>([]);
  const [statusLoading, setStatusLoading] = useState(false);

  // Load migrations first (fast), then status in background
  const fetchMigrations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const migrationsData = await migrationApi.getAll();
      setMigrations(migrationsData);
    } catch (err: any) {
      console.error('Error fetching migrations:', err);
      setError(err?.response?.data || 'Kunne ikke hente migrasjonsdata.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      setStatusLoading(true);
      const statusData = await migrationApi.getStatus();
      setTenantStatuses(statusData);
    } catch (err: any) {
      console.error('Error fetching status:', err);
      // Don't set main error - status can fail independently
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    await fetchMigrations();
    fetchStatus(); // Fire and forget - loads in background
  }, [fetchMigrations, fetchStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Form handlers ---
  const openCreateForm = () => {
    const nextSort = migrations.length > 0
      ? Math.max(...migrations.map(m => m.sortOrder)) + 10
      : 10;
    setFormData({
      migrationId: '',
      description: '',
      sqlScript: '',
      sortOrder: nextSort,
      createdBy: '',
      isActive: true
    });
    setEditingMigration(null);
    setShowForm(true);
  };

  const openEditForm = (migration: SchemaMigration) => {
    setFormData({
      migrationId: migration.migrationId,
      description: migration.description || '',
      sqlScript: migration.sqlScript,
      sortOrder: migration.sortOrder,
      createdBy: migration.createdBy || '',
      isActive: migration.isActive
    });
    setEditingMigration(migration);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editingMigration) {
        await migrationApi.update(editingMigration.migrationId, formData);
      } else {
        await migrationApi.create(formData);
      }
      setShowForm(false);
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data || 'Kunne ikke lagre migrasjon.');
    }
  };

  const handleDelete = async (migrationId: string) => {
    if (!window.confirm(`Slett migrasjon "${migrationId}"?`)) return;
    try {
      await migrationApi.delete(migrationId);
      await fetchData();
    } catch (err: any) {
      setError(err?.response?.data || 'Kunne ikke slette migrasjon.');
    }
  };

  // --- Run handlers ---
  const handlePreview = async () => {
    try {
      setRunning(true);
      setRunResult(null);
      const result = await migrationApi.run({
        tenantIds: selectedTenantIds.length > 0 ? selectedTenantIds : undefined,
        previewOnly: true
      });
      setRunResult(result);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Preview feilet.');
    } finally {
      setRunning(false);
    }
  };

  const handleRun = async () => {
    if (!window.confirm('Er du sikker på at du vil kjøre migrasjoner? Dette endrer databasene.')) return;
    try {
      setRunning(true);
      setRunResult(null);
      const result = await migrationApi.run({
        tenantIds: selectedTenantIds.length > 0 ? selectedTenantIds : undefined,
        previewOnly: false
      });
      setRunResult(result);
      await fetchData(); // Refresh status
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Migrering feilet.');
    } finally {
      setRunning(false);
    }
  };

  const toggleTenantSelection = (tenantId: number) => {
    setSelectedTenantIds(prev =>
      prev.includes(tenantId)
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const selectAllTenants = () => {
    if (selectedTenantIds.length === tenantStatuses.length) {
      setSelectedTenantIds([]);
    } else {
      setSelectedTenantIds(tenantStatuses.map(t => t.tenantId));
    }
  };

  // --- Loading/Error ---
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-600">Laster migrasjonsdata...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-600">{error}</p>
        <Button variant="secondary" onClick={() => { setError(null); fetchData(); }} className="mt-4">
          Prøv igjen
        </Button>
      </div>
    );
  }

  const totalPending = tenantStatuses.reduce((sum, t) => sum + t.pendingMigrations, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Migrasjoner</h2>
          <p className="text-sm text-gray-500 mt-1">
            Administrer og kjør databasemigrasjoner for alle tenanter
          </p>
        </div>
        <Button variant="secondary" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Oppdater
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-500">Migrasjoner</div>
          <div className="text-2xl font-bold text-gray-900">{migrations.length}</div>
          <div className="text-xs text-gray-400">{migrations.filter(m => m.isActive).length} aktive</div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-500">Tenanter</div>
          <div className="text-2xl font-bold text-gray-900">
            {statusLoading ? <span className="animate-pulse text-gray-400">...</span> : tenantStatuses.length}
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-500">Pending totalt</div>
          <div className={`text-2xl font-bold ${totalPending > 0 ? 'text-orange-600' : 'text-green-600'}`}>
            {statusLoading ? <span className="animate-pulse text-gray-400">...</span> : totalPending}
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-500">Oppdaterte</div>
          <div className="text-2xl font-bold text-green-600">
            {statusLoading ? <span className="animate-pulse text-gray-400">...</span> : `${tenantStatuses.filter(t => t.pendingMigrations === 0).length} / ${tenantStatuses.length}`}
          </div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {(['migrations', 'status', 'run'] as const).map(section => (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeSection === section
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {section === 'migrations' ? 'Migrasjoner' : section === 'status' ? 'Statusmatrise' : 'Kjør'}
            </button>
          ))}
        </nav>
      </div>

      {/* Migrations List */}
      {activeSection === 'migrations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button variant="primary" onClick={openCreateForm}>
              <Plus className="w-4 h-4 mr-2" />
              Ny migrasjon
            </Button>
          </div>

          {/* Form */}
          {showForm && (
            <MigrationForm
              formData={formData}
              setFormData={setFormData}
              isEditing={!!editingMigration}
              onSave={handleSave}
              onCancel={() => setShowForm(false)}
            />
          )}

          {/* List */}
          <div className="overflow-hidden border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rekkefølge</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Beskrivelse</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktiv</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opprettet</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Handlinger</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {migrations.map(m => (
                  <MigrationRow
                    key={m.migrationId}
                    migration={m}
                    onEdit={openEditForm}
                    onDelete={handleDelete}
                  />
                ))}
                {migrations.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      Ingen migrasjoner ennå. Klikk "Ny migrasjon" for å legge til.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Status Matrix */}
      {activeSection === 'status' && (
        statusLoading && tenantStatuses.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-3"></div>
            <span className="text-gray-500">Henter tenantstatus...</span>
          </div>
        ) : (
          <StatusMatrix migrations={migrations} tenantStatuses={tenantStatuses} />
        )
      )}

      {/* Run Migrations */}
      {activeSection === 'run' && (
        <RunSection
          tenantStatuses={tenantStatuses}
          selectedTenantIds={selectedTenantIds}
          toggleTenantSelection={toggleTenantSelection}
          selectAllTenants={selectAllTenants}
          onPreview={handlePreview}
          onRun={handleRun}
          running={running}
          runResult={runResult}
        />
      )}
    </div>
  );
};

// --- Sub-components ---

const MigrationRow: React.FC<{
  migration: SchemaMigration;
  onEdit: (m: SchemaMigration) => void;
  onDelete: (id: string) => void;
}> = ({ migration, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-4 py-3 text-sm text-gray-900">{migration.sortOrder}</td>
        <td className="px-4 py-3 text-sm font-mono text-gray-900">{migration.migrationId}</td>
        <td className="px-4 py-3 text-sm text-gray-600">{migration.description || '-'}</td>
        <td className="px-4 py-3">
          {migration.isActive ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Aktiv</span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Inaktiv</span>
          )}
        </td>
        <td className="px-4 py-3 text-sm text-gray-500">
          {new Date(migration.createdOn).toLocaleDateString('nb-NO')}
        </td>
        <td className="px-4 py-3 text-right space-x-2">
          <button onClick={() => setExpanded(!expanded)} className="text-gray-400 hover:text-gray-600" title="Vis SQL">
            {expanded ? <ChevronUp className="w-4 h-4 inline" /> : <ChevronDown className="w-4 h-4 inline" />}
          </button>
          <button onClick={() => onEdit(migration)} className="text-blue-500 hover:text-blue-700" title="Rediger">
            <Edit3 className="w-4 h-4 inline" />
          </button>
          <button onClick={() => onDelete(migration.migrationId)} className="text-red-500 hover:text-red-700" title="Slett">
            <Trash2 className="w-4 h-4 inline" />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="px-4 py-3 bg-gray-50">
            <pre className="text-xs font-mono whitespace-pre-wrap text-gray-700 max-h-60 overflow-y-auto">
              {migration.sqlScript}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
};

const MigrationForm: React.FC<{
  formData: MigrationDto;
  setFormData: React.Dispatch<React.SetStateAction<MigrationDto>>;
  isEditing: boolean;
  onSave: () => void;
  onCancel: () => void;
}> = ({ formData, setFormData, isEditing, onSave, onCancel }) => {
  return (
    <div className="bg-white border rounded-lg p-6 space-y-4">
      <h3 className="text-lg font-medium">{isEditing ? 'Rediger migrasjon' : 'Ny migrasjon'}</h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Migration ID</label>
          <input
            type="text"
            value={formData.migrationId}
            onChange={e => setFormData(prev => ({ ...prev, migrationId: e.target.value }))}
            disabled={isEditing}
            placeholder="f.eks. 001_CreateUsers"
            className="w-full border rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rekkefølge</label>
          <input
            type="number"
            value={formData.sortOrder}
            onChange={e => setFormData(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
            className="w-full border rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={e => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Aktiv</span>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivelse</label>
        <input
          type="text"
          value={formData.description || ''}
          onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Kort beskrivelse av hva migrasjonen gjør"
          className="w-full border rounded-md px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">SQL Script</label>
        <textarea
          value={formData.sqlScript}
          onChange={e => setFormData(prev => ({ ...prev, sqlScript: e.target.value }))}
          rows={12}
          placeholder="-- Idempotent SQL her (bruk IF NOT EXISTS)..."
          className="w-full border rounded-md px-3 py-2 text-sm font-mono"
        />
      </div>

      <div className="flex justify-end space-x-3">
        <Button variant="ghost" onClick={onCancel}>
          <X className="w-4 h-4 mr-1" /> Avbryt
        </Button>
        <Button
          variant="primary"
          onClick={onSave}
          disabled={!formData.migrationId || !formData.sqlScript}
        >
          <Save className="w-4 h-4 mr-1" /> {isEditing ? 'Oppdater' : 'Opprett'}
        </Button>
      </div>
    </div>
  );
};

const StatusMatrix: React.FC<{
  migrations: SchemaMigration[];
  tenantStatuses: TenantMigrationStatus[];
}> = ({ migrations, tenantStatuses }) => {
  const activeMigrations = migrations.filter(m => m.isActive);

  if (activeMigrations.length === 0 || tenantStatuses.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Ingen data å vise. Legg til migrasjoner og/eller tenanter først.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 z-10">
              Tenant
            </th>
            {activeMigrations.map(m => (
              <th key={m.migrationId} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase" title={m.description || ''}>
                {m.migrationId.length > 20 ? m.migrationId.substring(0, 20) + '...' : m.migrationId}
              </th>
            ))}
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {tenantStatuses.map(tenant => (
            <tr key={tenant.tenantId} className="hover:bg-gray-50">
              <td className="px-4 py-2 text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">
                {tenant.tenantName}
                <span className="block text-xs text-gray-400">{tenant.databaseName}</span>
              </td>
              {activeMigrations.map(m => {
                const isApplied = tenant.appliedMigrationIds.includes(m.migrationId);
                return (
                  <td key={m.migrationId} className="px-3 py-2 text-center">
                    {isApplied ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500 inline" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400 inline" />
                    )}
                  </td>
                );
              })}
              <td className="px-4 py-2 text-center">
                {tenant.pendingMigrations === 0 ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    OK
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    {tenant.pendingMigrations} pending
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RunSection: React.FC<{
  tenantStatuses: TenantMigrationStatus[];
  selectedTenantIds: number[];
  toggleTenantSelection: (id: number) => void;
  selectAllTenants: () => void;
  onPreview: () => void;
  onRun: () => void;
  running: boolean;
  runResult: MigrationRunResult | null;
}> = ({ tenantStatuses, selectedTenantIds, toggleTenantSelection, selectAllTenants, onPreview, onRun, running, runResult }) => {
  const tenantsWithPending = tenantStatuses.filter(t => t.pendingMigrations > 0);

  return (
    <div className="space-y-6">
      {/* Tenant selection */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-medium text-gray-700">
            Velg tenanter ({selectedTenantIds.length === 0 ? 'alle' : selectedTenantIds.length + ' valgt'})
          </h3>
          <button onClick={selectAllTenants} className="text-sm text-blue-600 hover:text-blue-800">
            {selectedTenantIds.length === tenantStatuses.length ? 'Fjern alle' : 'Velg alle'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-60 overflow-y-auto">
          {tenantStatuses.map(tenant => (
            <label
              key={tenant.tenantId}
              className={`flex items-center space-x-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                tenant.pendingMigrations > 0 ? 'bg-orange-50' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={selectedTenantIds.includes(tenant.tenantId)}
                onChange={() => toggleTenantSelection(tenant.tenantId)}
                className="rounded"
              />
              <span className="text-sm text-gray-900">{tenant.tenantName}</span>
              {tenant.pendingMigrations > 0 && (
                <span className="text-xs text-orange-600">({tenant.pendingMigrations} pending)</span>
              )}
            </label>
          ))}
        </div>

        <p className="text-xs text-gray-500 mt-2">
          Ingen avhuking = alle tenanter. {tenantsWithPending.length} tenant(er) har ventende migrasjoner.
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex space-x-4">
        <Button variant="secondary" onClick={onPreview} disabled={running}>
          <Eye className="w-4 h-4 mr-2" />
          {running ? 'Kjører...' : 'Preview'}
        </Button>
        <Button variant="success" onClick={onRun} disabled={running}>
          <Play className="w-4 h-4 mr-2" />
          {running ? 'Kjører...' : 'Kjør migrasjoner'}
        </Button>
      </div>

      {/* Results */}
      {runResult && (
        <div className={`border rounded-lg p-4 ${runResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center space-x-2 mb-3">
            {runResult.success ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600" />
            )}
            <span className="font-medium">{runResult.message}</span>
          </div>

          {runResult.errors.length > 0 && (
            <div className="mb-3">
              <h4 className="text-sm font-medium text-red-700 mb-1">Feil:</h4>
              <ul className="text-sm text-red-600 list-disc list-inside">
                {runResult.errors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </div>
          )}

          {runResult.details.length > 0 && (
            <div className="overflow-hidden border rounded-lg mt-3">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Tenant</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Migrasjoner</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Tid</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {runResult.details.map(detail => (
                    <tr key={detail.tenantId}>
                      <td className="px-4 py-2 text-sm">
                        {detail.tenantName}
                        <span className="block text-xs text-gray-400">{detail.databaseName}</span>
                      </td>
                      <td className="px-4 py-2">
                        {detail.success ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500 inline" />
                        ) : (
                          <span className="text-red-600 text-sm">{detail.error}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {detail.appliedMigrations.length > 0
                          ? detail.appliedMigrations.join(', ')
                          : <span className="text-gray-400">Ingen pending</span>
                        }
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {detail.totalTimeMs > 0 && (
                          <span className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {detail.totalTimeMs}ms
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MigrationDashboard;
